import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, GraduationCap } from "lucide-react";
import axios from "axios";
import { getAuthHeaders } from "@/utils/auth";
import Header from "@/components/Header";

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const ThesisDissertationForm = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [formData, setFormData] = useState({
    contact_name: "",
    contact_affiliation: "",
    contact_email: "",
    course: "",
    course_duration: "",
    speciality: "",
    areas_of_interest: "",
    topics_interested: "",
    expected_support: "",
    deadlines: ""
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          variant: "destructive",
          title: "File too large!",
          description: `Maximum size is 10 MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)} MB`,
        });
        e.target.value = '';
        setSelectedFile(null);
      } else {
        setSelectedFile(file);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("contact_name", formData.contact_name);
      formDataToSend.append("contact_affiliation", formData.contact_affiliation);
      formDataToSend.append("contact_email", formData.contact_email);
      formDataToSend.append("course", formData.course);
      formDataToSend.append("course_duration", formData.course_duration);
      formDataToSend.append("speciality", formData.speciality);
      formDataToSend.append("areas_of_interest", formData.areas_of_interest);
      formDataToSend.append("topics_interested", formData.topics_interested);
      formDataToSend.append("expected_support", formData.expected_support);
      formDataToSend.append("deadlines", formData.deadlines);
      
      if (selectedFile) {
        formDataToSend.append("file", selectedFile);
      }

      await axios.post(`${API}/api/submit/thesis-dissertation`, formDataToSend, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });

      toast({
        title: "Submission successful!",
        description: "Your thesis and dissertation support request has been submitted.",
      });

      navigate("/dashboard");
    } catch (error) {
      toast({
        title: "Submission failed",
        description: error.response?.data?.detail || "An error occurred",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Header />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        <Card className="shadow-xl">
          <CardHeader className="space-y-4">
            <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center mb-4">
              <GraduationCap className="h-6 w-6 text-purple-600" />
            </div>
            <CardTitle className="text-xl sm:text-2xl" >
              Research Mentorship & Academic Guidance
            </CardTitle>
            <CardDescription className="text-base">
              End-to-end mentorship for students, early-career researchers, and experienced academics
            </CardDescription>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-gray-700 italic">
                <strong>Examples:</strong> Refining research questions, methodology development, guidance for theses, dissertations, 
                and capstone projects, structuring literature reviews, support for ethics submissions, manuscript development, 
                conference abstracts and presentations
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
                
                <div>
                  <Label htmlFor="contact_name">Name *</Label>
                  <Input
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    required
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <Label htmlFor="contact_affiliation">Affiliation/Organization *</Label>
                  <Input
                    id="contact_affiliation"
                    value={formData.contact_affiliation}
                    onChange={(e) => setFormData({ ...formData, contact_affiliation: e.target.value })}
                    required
                    placeholder="Your university or institution"
                  />
                </div>

                <div>
                  <Label htmlFor="contact_email">Email *</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    required
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              {/* Course Details Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Course Details</h3>
                
                <div>
                  <Label htmlFor="course">Course *</Label>
                  <Input
                    id="course"
                    value={formData.course}
                    onChange={(e) => setFormData({ ...formData, course: e.target.value })}
                    required
                    placeholder="e.g., Master of Public Health, PhD in Dentistry"
                  />
                </div>

                <div>
                  <Label htmlFor="course_duration">Course Duration *</Label>
                  <Input
                    id="course_duration"
                    value={formData.course_duration}
                    onChange={(e) => setFormData({ ...formData, course_duration: e.target.value })}
                    required
                    placeholder="e.g., 2 years, 3-4 years"
                  />
                </div>

                <div>
                  <Label htmlFor="speciality">Speciality *</Label>
                  <Input
                    id="speciality"
                    value={formData.speciality}
                    onChange={(e) => setFormData({ ...formData, speciality: e.target.value })}
                    required
                    placeholder="e.g., Epidemiology, Pediatric Dentistry, Clinical Research"
                  />
                </div>
              </div>

              {/* Research Details Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Research Details</h3>
                
                <div>
                  <Label htmlFor="areas_of_interest">Areas of Interest to Pursue as Thesis *</Label>
                  <Textarea
                    id="areas_of_interest"
                    value={formData.areas_of_interest}
                    onChange={(e) => setFormData({ ...formData, areas_of_interest: e.target.value })}
                    required
                    rows={4}
                    placeholder="Describe your areas of research interest and what you'd like to explore in your thesis..."
                  />
                </div>

                <div>
                  <Label htmlFor="topics_interested">List of Topics Interested *</Label>
                  <Textarea
                    id="topics_interested"
                    value={formData.topics_interested}
                    onChange={(e) => setFormData({ ...formData, topics_interested: e.target.value })}
                    required
                    rows={4}
                    placeholder="List specific research topics or questions you're interested in exploring..."
                  />
                </div>

                <div>
                  <Label htmlFor="expected_support">Expected Support *</Label>
                  <Textarea
                    id="expected_support"
                    value={formData.expected_support}
                    onChange={(e) => setFormData({ ...formData, expected_support: e.target.value })}
                    required
                    rows={4}
                    placeholder="What kind of support do you need? (e.g., topic selection, methodology design, literature review, data analysis, writing guidance)"
                  />
                </div>

                <div>
                  <Label htmlFor="deadlines">Any Deadlines</Label>
                  <Input
                    id="deadlines"
                    value={formData.deadlines}
                    onChange={(e) => setFormData({ ...formData, deadlines: e.target.value })}
                    placeholder="e.g., Proposal submission by March 2025, Final thesis by December 2025"
                  />
                </div>
              </div>

              {/* Disclaimer Section */}
              <div className="border-t pt-4">
                <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="disclaimer-checkbox"
                    checked={disclaimerAccepted}
                    onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    required
                  />
                  <label htmlFor="disclaimer-checkbox" className="text-sm text-gray-700">
                    I/we agree with the{" "}
                    <button
                      type="button"
                      onClick={() => setShowDisclaimerModal(true)}
                      className="text-blue-600 hover:text-blue-800 underline font-medium"
                    >
                      disclaimer
                    </button>
                    {" "}by the EviDent Research Consultancy Ltd
                  </label>
                </div>
              </div>

              {/* File Upload Section */}
              <div>
                <Label htmlFor="file">Attach Related Documents (Optional)</Label>
                <Input
                  id="file"
                  type="file"
                  onChange={handleFileChange}
                  className="mt-2"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Upload any relevant documents (e.g., course outline, preliminary proposal, assignment briefs) (Max 10MB)
                </p>
              </div>

              {/* Submit Buttons */}
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <Button
                  type="submit"
                  data-testid="submit-thesis-btn"
                  disabled={submitting || !disclaimerAccepted}
                  className="bg-purple-600 hover:bg-purple-700 w-full sm:flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Submitting..." : "Submit Request"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/dashboard")}
                  className="w-full sm:flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Disclaimer Modal */}
      <Dialog open={showDisclaimerModal} onOpenChange={setShowDisclaimerModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Research Advice Disclaimer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
              <p className="text-sm sm:text-base leading-relaxed text-gray-800">
                The information, guidance, and support provided by <strong>EVIDENT RESEARCH CONSULTANCY LTD</strong>, 
                including evidence synthesis, research methods consultancy, training, and related services, are intended 
                for general informational and educational purposes only. We do not provide legal, clinical, or medical 
                advice, and our input should not be relied upon as a substitute for professional judgement in those areas.
              </p>
            </div>
            
            <p className="text-sm sm:text-base leading-relaxed text-gray-700">
              All decisions based on the advice or materials we provide must be independently reviewed and validated by 
              the client. EVIDENT RESEARCH CONSULTANCY LTD accepts no liability for actions taken or not taken based on 
              the use of our research advice or outputs.
            </p>

            <div className="flex justify-end mt-6">
              <Button
                onClick={() => setShowDisclaimerModal(false)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ThesisDissertationForm;
